// =============================================================================
// pages/EngagementRequests.jsx — Admin view of customer engagement requests
// =============================================================================
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import '../styles/EngagementRequests.css';

const STATUS_CONFIG = {
  pending_admin:       { label: 'Pending Review',     color: 'var(--amber)',  bg: 'var(--amber-bg)',  border: 'rgba(245,166,35,0.3)'  },
  ca_assigned:         { label: 'CA Assigned',        color: 'var(--accent)', bg: 'var(--blue-bg)',   border: 'rgba(79,142,247,0.3)'  },
  engagement_created:  { label: 'Engagement Created', color: 'var(--green)',  bg: 'var(--green-bg)',  border: 'rgba(62,207,122,0.3)'  },
  rejected:            { label: 'Rejected',            color: 'var(--red)',    bg: 'var(--red-bg)',    border: 'rgba(224,82,82,0.3)'   },
};

const SERVICE_LABELS = {
  general_audit:    'General Audit',
  tax_audit:        'Tax Audit',
  gst_filing:       'GST Filing',
  statutory_audit:  'Statutory Audit',
  due_diligence:    'Due Diligence',
  other:            'Other',
};

function StatusBadge({ status }) {
  const c = STATUS_CONFIG[status] || STATUS_CONFIG.pending_admin;
  return (
    <span className="er-badge" style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}` }}>
      {c.label}
    </span>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ── Assign CA Modal ───────────────────────────────────────────────────────────
function AssignModal({ request, onClose, onAssigned }) {
  const [empanelledCAs, setEmpanelledCAs] = useState([]);
  const [loading,        setLoading]       = useState(true);
  const [selectedCA,     setSelectedCA]    = useState('');
  const [adminNote,      setAdminNote]     = useState('');
  const [saving,         setSaving]        = useState(false);
  const [error,          setError]         = useState('');

  useEffect(() => {
    api.listEmpanelledCAs().then(rows => {
      setEmpanelledCAs(rows.filter(r => r.user_id)); // only CAs with portal accounts
      setLoading(false);
    }).catch(e => { setError(e.message); setLoading(false); });
  }, []);

  const submit = async () => {
    if (!selectedCA) return setError('Please select a CA');
    setSaving(true); setError('');
    try {
      const result = await api.assignEngagementRequest(request.id, {
        empanelled_ca_id: selectedCA,
        admin_note: adminNote,
      });
      onAssigned(result);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="er-modal-backdrop" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="er-modal">
        <div className="er-modal-header">
          <h3>Assign CA to Request</h3>
          <button className="er-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="er-modal-body">
          <div className="er-req-summary">
            <div className="er-req-biz">{request.business_name}</div>
            <div className="er-req-meta">
              {request.customer_name} · {request.customer_email}
              {request.pan_number && <> · PAN: <span className="er-mono">{request.pan_number}</span></>}
            </div>
            {request.description && (
              <div className="er-req-desc">"{request.description}"</div>
            )}
          </div>

          {loading ? (
            <div className="er-loading">Loading empanelled CAs…</div>
          ) : empanelledCAs.length === 0 ? (
            <div className="er-empty-cas">
              No empanelled CAs with active accounts. Add CAs from the Empanelled CAs page first.
            </div>
          ) : (
            <div className="er-field">
              <label>Select Empanelled CA <span className="er-required">*</span></label>
              <select
                className="er-select"
                value={selectedCA}
                onChange={e => setSelectedCA(e.target.value)}
              >
                <option value="">— Choose a CA —</option>
                {empanelledCAs.map(ca => {
                  const isCARole = ['ca', 'admin'].includes(ca.linked_user_role);
                  return (
                    <option key={ca.id} value={ca.id}>
                      {ca.name || ca.linked_user_name || ca.email} — ICAI {ca.icai_number}
                      {!isCARole ? ' ⚠ (needs CA upgrade)' : ''}
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          <div className="er-field">
            <label>Admin Note (optional)</label>
            <textarea
              className="er-textarea"
              rows={3}
              placeholder="Any notes for internal reference…"
              value={adminNote}
              onChange={e => setAdminNote(e.target.value)}
            />
          </div>

          {error && <div className="er-error">{error}</div>}

          <p className="er-note">
            Assigning a CA will create an engagement and send the customer an email
            with their Engagement ID.
          </p>
        </div>
        <div className="er-modal-footer">
          <button className="er-btn er-btn--secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="er-btn er-btn--primary" onClick={submit} disabled={saving || !selectedCA}>
            {saving ? 'Assigning…' : 'Assign & Create Engagement'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Reject Modal ──────────────────────────────────────────────────────────────
function RejectModal({ request, onClose, onRejected }) {
  const [note,    setNote]    = useState('');
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  const submit = async () => {
    setSaving(true); setError('');
    try {
      await api.rejectEngagementRequest(request.id, note);
      onRejected(request.id);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="er-modal-backdrop" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="er-modal">
        <div className="er-modal-header">
          <h3>Reject Request</h3>
          <button className="er-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="er-modal-body">
          <p className="er-confirm-text">
            Are you sure you want to reject the request for <strong>{request.business_name}</strong>?
            The customer will be notified.
          </p>
          <div className="er-field">
            <label>Reason (optional)</label>
            <textarea
              className="er-textarea"
              rows={3}
              placeholder="Reason for rejection…"
              value={note}
              onChange={e => setNote(e.target.value)}
            />
          </div>
          {error && <div className="er-error">{error}</div>}
        </div>
        <div className="er-modal-footer">
          <button className="er-btn er-btn--secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="er-btn er-btn--danger" onClick={submit} disabled={saving}>
            {saving ? 'Rejecting…' : 'Reject Request'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function EngagementRequests({ userRole }) {
  const [rows,        setRows]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [statusFilter,setStatusFilter]= useState('');
  const [assigning,   setAssigning]   = useState(null);
  const [rejecting,   setRejecting]   = useState(null);
  const [error,       setError]       = useState('');
  const [pageSize,    setPageSize]    = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  const load = useCallback(async (s = '') => {
    setLoading(true); setError(''); setCurrentPage(1);
    try {
      const data = await api.listEngagementRequests(s || null);
      setRows(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(statusFilter); }, [load, statusFilter]);

  const handleAssigned = (result) => {
    setRows(r => r.map(x =>
      x.id === assigning?.id
        ? { ...x, status: 'engagement_created', engagement_number: result.engagement_number }
        : x
    ));
  };

  const handleRejected = (id) => {
    setRows(r => r.map(x => x.id === id ? { ...x, status: 'rejected' } : x));
  };

  const pendingCount = rows.filter(r => r.status === 'pending_admin').length;
  const totalPages   = Math.max(1, Math.ceil(rows.length / pageSize));
  const startIdx     = (currentPage - 1) * pageSize;
  const endIdx       = Math.min(startIdx + pageSize, rows.length);
  const visibleRows  = rows.slice(startIdx, endIdx);

  if (!userRole?.isAdmin) {
    return <div className="er-wrap"><p className="er-error er-error--block">Admin access required.</p></div>;
  }

  return (
    <div className="er-wrap">
      <div className="er-header">
        <div>
          <h2 className="er-title">
            Engagement Requests
            {pendingCount > 0 && <span className="er-pending-badge">{pendingCount} pending</span>}
          </h2>
          <p className="er-subtitle">Customer-initiated requests awaiting CA assignment.</p>
        </div>
      </div>

      <div className="er-toolbar">
        <div className="er-filters">
          {['', 'pending_admin', 'engagement_created', 'rejected'].map(s => (
            <button
              key={s}
              className={`er-filter-btn${statusFilter === s ? ' er-filter-btn--active' : ''}`}
              onClick={() => setStatusFilter(s)}
            >
              {s === '' ? 'All' : STATUS_CONFIG[s]?.label || s}
            </button>
          ))}
        </div>
        <div className="er-toolbar-right">
          <div className="er-page-size">
            <span>Show</span>
            <select
              className="er-page-size-select"
              value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
            >
              {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <span>per page</span>
          </div>
          <span className="er-count">
            {rows.length === 0 ? '0 requests' : `${startIdx + 1}–${endIdx} of ${rows.length}`}
          </span>
        </div>
      </div>

      {error && <div className="er-error er-error--block">{error}</div>}

      {loading ? (
        <div className="er-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="er-empty">
          {statusFilter ? `No ${STATUS_CONFIG[statusFilter]?.label?.toLowerCase()} requests.` : 'No engagement requests yet.'}
        </div>
      ) : (
        <div className="er-cards">
          {visibleRows.map(r => (
            <div key={r.id} className="er-card">
              <div className="er-card-top">
                <div className="er-card-left">
                  <div className="er-biz-name">{r.business_name}</div>
                  <div className="er-req-id">{r.request_id}</div>
                </div>
                <StatusBadge status={r.status} />
              </div>

              <div className="er-card-meta">
                <span><strong>Customer:</strong> {r.customer_name || r.customer_email}</span>
                {r.pan_number && <span><strong>PAN:</strong> <span className="er-mono">{r.pan_number}</span></span>}
                {r.gstin && <span><strong>GSTIN:</strong> <span className="er-mono">{r.gstin}</span></span>}
                <span><strong>Service:</strong> {SERVICE_LABELS[r.service_type] || r.service_type}</span>
                <span><strong>Requested:</strong> {fmtDate(r.created_at)}</span>
              </div>

              {r.description && (
                <div className="er-desc">"{r.description}"</div>
              )}

              {r.status === 'engagement_created' && r.ca_name && (
                <div className="er-assigned-info">
                  Assigned to <strong>{r.ca_name}</strong>
                  {r.engagement_number && (
                    <> · <span className="er-mono">EN-{String(r.engagement_number).padStart(8,'0')}</span></>
                  )}
                  {r.engagement_status && (
                    <> · {r.engagement_status.replace(/_/g, ' ')}</>
                  )}
                </div>
              )}
              {r.sync_responsibility && (
                <div className="er-admin-note" style={{ fontStyle: 'normal', fontSize: 11 }}>
                  Sync preference: <strong>{r.sync_responsibility === 'ca' ? 'CA syncs on behalf' : 'Customer self-sync'}</strong>
                  {r.data_sharing_consent && <> · Consent recorded</>}
                </div>
              )}

              {r.admin_note && (
                <div className="er-admin-note">Note: {r.admin_note}</div>
              )}

              {r.status === 'pending_admin' && (
                <div className="er-card-actions">
                  <button
                    className="er-btn er-btn--primary er-btn--sm"
                    onClick={() => setAssigning(r)}
                  >
                    Assign CA
                  </button>
                  <button
                    className="er-btn er-btn--danger er-btn--sm"
                    onClick={() => setRejecting(r)}
                  >
                    Reject
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!loading && rows.length > 0 && totalPages > 1 && (
        <div className="er-pagination">
          <button
            className="er-page-btn"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            ← Prev
          </button>
          <span className="er-page-info">Page {currentPage} of {totalPages}</span>
          <button
            className="er-page-btn"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next →
          </button>
        </div>
      )}

      {assigning && (
        <AssignModal
          request={assigning}
          onClose={() => setAssigning(null)}
          onAssigned={handleAssigned}
        />
      )}

      {rejecting && (
        <RejectModal
          request={rejecting}
          onClose={() => setRejecting(null)}
          onRejected={handleRejected}
        />
      )}
    </div>
  );
}
